import React from 'react';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useAssessmentSubmissionsQuery } from '@uplift/queries';
import { AssessmentKeys } from '@uplift/types';
import { Loader } from '@uplift/ui-v2';
import { CalendarDateService } from '@uplift/services';
import { SourceChipFilterValue } from './ChipFilters';
import { AssessmentSelect, SourceSelect } from './HistoryFilters';
import { AssessmentSubmission } from '@uplift/api';
import { mapAssessmentScoreToDescription } from '@uplift/utils';
import { useL10n } from '@uplift/hooks';
import { useAssessmentOptions } from './hooks';


const AssessmentHistoryRow: React.VFC<{ assessmentKey: AssessmentKeys; assessmentSubmission: AssessmentSubmission }> = ({ assessmentKey, assessmentSubmission }) => {
  const l10n = useL10n();
  const dateCell = React.useMemo(() => {
    if (assessmentSubmission.submission.completedAt) {
      return <Typography>{CalendarDateService.date(assessmentSubmission.submission.completedAt).format('MMM D')}</Typography>;
    }
    if (assessmentSubmission.submission.expiredAt) {
      return <Typography variant="body2" color="error">Expired {CalendarDateService.date(assessmentSubmission.submission.expiredAt).format('MMM D')}</Typography>;
    }

    return <Typography color="textDisabled">Not completed</Typography>;
  }, [ assessmentSubmission ]);

  return (
    <TableRow>
      <TableCell>
        {dateCell}
      </TableCell>
      <TableCell><Typography>{assessmentSubmission.score}</Typography></TableCell>
      <TableCell>{mapAssessmentScoreToDescription(assessmentKey, l10n)?.(assessmentSubmission.score) ?? '-'}</TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography>{assessmentSubmission.submission.userId === assessmentSubmission.submission.processedBy
          ? 'Patient-completed'
          : 'Clinician-administered'}
        </Typography>
      </TableCell>
    </TableRow>
  );
};

const AssessmentsHistoryTable: React.VFC<{ patientId: string; assessmentKey: AssessmentKeys; filter?: SourceChipFilterValue }> = (props) => {
  const { data: _submissions } = useAssessmentSubmissionsQuery(props.patientId, props.assessmentKey, true);

  const submissions = React.useMemo(() => {
    if (props.filter === 'patient') {
      return _submissions.filter(s => s.submission.userId === s.submission.processedBy);
    }
    if (props.filter === 'administer') {
      return _submissions.filter(s => s.submission.userId !== s.submission.processedBy);
    }

    return _submissions;
  }, [ props.filter, _submissions ]);

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ minWidth: '54px', width: '54px' }}>DATE</TableCell>
          <TableCell>SCORE</TableCell>
          <TableCell>BAND</TableCell>
          <TableCell sx={{ width: '100%', whiteSpace: 'nowrap' }}>SOURCE</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {submissions.length ? submissions.map(submission => (
          <AssessmentHistoryRow
            key={submission.submission.formSubmissionId}
            assessmentKey={props.assessmentKey}
            assessmentSubmission={submission}
          />
        )) : (
          <TableRow>
            <TableCell colSpan={4}>
              <Typography color="textDisabled" variant="h5" textAlign="center" sx={{ p: 3 }}>
                No completed assessments
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};


export const HistoryTab: React.VFC<{ patientId: string }> = (props) => {
  const options = useAssessmentOptions(props.patientId);
  const [ assessmentId, setAssessmentId ] = React.useState(options[0].id);
  const [ completedByFilter, setCompletedByFilter ] = React.useState<SourceChipFilterValue>();
  const assessment = options.find(option => option.id === assessmentId) ?? options[0];

  return (
    <Stack gap={2} sx={{ p: 2 }}>
      <Stack direction="row" gap={2}>
        <AssessmentSelect options={options} value={assessment} setValue={option => setAssessmentId(option.id)} />
        <SourceSelect value={completedByFilter} setValue={setCompletedByFilter} />
      </Stack>

      <React.Suspense fallback={<Loader height="300px" color="primary" />}>
        <AssessmentsHistoryTable
          patientId={props.patientId}
          assessmentKey={assessment.key}
          filter={completedByFilter}
        />
      </React.Suspense>
    </Stack>
  );
};
